/**
 * Ciclo de vida de uma plataforma Angular montada pelo React, compartilhado pelo
 * AppModule e pela folha.
 *
 * Resolve dois problemas que a guarda de idempotência dentro do bootstrap não
 * consegue resolver sozinha, porque ela só enxerga estado publicado depois do
 * `await bootstrapModule`:
 *
 * 1. **Serialização.** Mount e unmount mexem nos mesmos singletons. Um unmount que
 *    cai na janela em que o bootstrap está em voo destrói uma plataforma
 *    meio-inicializada e deixa o mount concluir, republicando referências de um
 *    módulo já morto. Uma operação por vez garante que toda guarda avalie estado
 *    estável.
 *
 * 2. **Ciclo mount → cleanup → mount no mesmo nó.** O StrictMode executa esse ciclo
 *    a cada montagem, em um único bloco síncrono. Destruir para rebootstrapar no
 *    mesmo container chega ao mesmo estado final pagando um bootstrap inteiro — e
 *    outra rodada das chamadas de inicialização, que é o que aparece duplicado no
 *    Network. O teardown só decide se vai acontecer fora do bloco síncrono que o
 *    pediu, então um mount no mesmo container o cancela antes.
 *
 * A identidade do container é o que separa os dois casos: no ciclo do StrictMode o
 * componente React é o mesmo e o nó também, enquanto um remonte de verdade (a troca
 * de `key` que reroteia a folha) cria um nó novo. Sem essa distinção, cancelar o
 * teardown engoliria o remonte e a folha ficaria na rota anterior.
 *
 * Por isso a cancelação é registrada por requisição, com o container a que ela se
 * refere, e não num sinalizador único. Num remonte sob StrictMode a sequência é
 * `unmount(antigo)`, `mount(novo)`, `unmount(novo)`, `mount(novo)`: com sinalizador
 * único o último mount cancelaria também o teardown do container antigo, deixando o
 * módulo velho vivo num nó que o React já removeu — tela vazia.
 */
export interface BootstrapLifecycle {
  /** Monta no container. Idempotente: repetir com a folha montada não rebootstrapa. */
  mount(container: HTMLElement): Promise<void>;
  /** Destrói o que está montado. Seguro sem mount prévio. */
  unmount(): Promise<void>;
}

export interface BootstrapOperations {
  /**
   * Cria a plataforma e bootstrapa o módulo. Deve se autoproteger quando já há algo
   * montado: o ciclo de vida evita o despacho redundante para o mesmo container, mas
   * a guarda própria é a rede para sequências de remonte fora do previsto.
   */
  bootstrap(container: HTMLElement): Promise<void>;
  /** Destrói módulo, plataforma e resíduos de DOM. */
  teardown(): Promise<void>;
}

/** Teardown pedido e ainda não decidido, junto do container que ele derrubaria. */
interface TeardownRequest {
  container: HTMLElement | null;
  cancelled: boolean;
}

export function createBootstrapLifecycle(operations: BootstrapOperations): BootstrapLifecycle {
  let pendingOperation: Promise<void> = Promise.resolve();
  let pendingTeardowns: TeardownRequest[] = [];
  // Dois registros de container, porque as duas decisões acontecem em momentos
  // diferentes: `requestedContainer` é escrito no instante da chamada e é o que
  // identifica o alvo de um teardown pedido no mesmo bloco síncrono;
  // `mountedContainer` reflete o que a fila efetivamente bootstrapou e é o que evita
  // um segundo bootstrap redundante.
  let requestedContainer: HTMLElement | null = null;
  let mountedContainer: HTMLElement | null = null;

  function enqueue(operation: () => Promise<void>): Promise<void> {
    const result = pendingOperation.then(operation);
    // A cadeia interna absorve a rejeição para que a falha de uma operação não
    // rejeite a seguinte; quem chamou continua recebendo o erro por `result`.
    pendingOperation = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  return {
    mount(container: HTMLElement): Promise<void> {
      // Cancela só os teardowns que derrubariam ESTE container. Um teardown de outro
      // container segue valendo: é o remonte, e engoli-lo deixaria a folha antiga viva
      // num nó descartado.
      pendingTeardowns
        .filter((request) => request.container === container)
        .forEach((request) => {
          request.cancelled = true;
        });
      requestedContainer = container;

      return enqueue(async () => {
        if (mountedContainer === container) {
          return;
        }
        try {
          await operations.bootstrap(container);
          mountedContainer = container;
        } catch (err) {
          // Montagem falhou: o nó deixa de valer como referência de folha viva, para
          // não cancelar um teardown legítimo mais adiante.
          if (requestedContainer === container) {
            requestedContainer = null;
          }
          throw err;
        }
      });
    },

    unmount(): Promise<void> {
      const request: TeardownRequest = { container: requestedContainer, cancelled: false };
      pendingTeardowns.push(request);

      return enqueue(async () => {
        pendingTeardowns = pendingTeardowns.filter((pending) => pending !== request);

        // Ponto de decisão, já fora do bloco síncrono que pediu o teardown: um mount
        // que tenha voltado a este mesmo container nesse meio-tempo já o cancelou.
        if (request.cancelled) {
          return;
        }
        if (requestedContainer === request.container) {
          requestedContainer = null;
        }
        mountedContainer = null;
        await operations.teardown();
      });
    },
  };
}
